import React, { useEffect, useMemo, useState } from "react";
import { Download, Search, RefreshCw, Image as ImageIcon, AlertTriangle, CheckCircle2, XCircle, Clock, CopyX, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api, openFileById } from "@/lib/api";

export const REJECT_REASONS = ["UTR not found", "Amount mismatch", "Duplicate UTR", "Other"];

export const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

function ProofBadge({ row }) {
  if (!row.proofFileId) return <span className="text-slate-300">—</span>;
  return (
    <button type="button" onClick={() => openFileById(row.proofFileId, row.proofName)}
      data-testid={`proof-${row.applicationNumber}`}
      className="inline-flex items-center gap-1 rounded-full bg-[#1b5e3f]/10 px-2.5 py-1 text-[12px] font-semibold text-[#1b5e3f] hover:bg-[#1b5e3f]/20">
      <ImageIcon className="h-3.5 w-3.5" /> Proof
    </button>
  );
}

function StatusPill({ s }) {
  const map = {
    verified: ["Verified", "bg-emerald-100 text-emerald-700"],
    rejected: ["Rejected", "bg-red-100 text-red-600"],
    pending: ["प्रलंबित", "bg-amber-100 text-amber-700"],
  };
  const [lbl, cls] = map[s] || map.pending;
  return <span className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${cls}`}>{lbl}</span>;
}

function SummaryCards({ rows }) {
  const pending = rows.filter((r) => r.paymentStatus === "pending" || r.sentForReview).length;
  const verified = rows.filter((r) => r.paymentStatus === "verified").length;
  const rejected = rows.filter((r) => r.paymentStatus === "rejected").length;
  const dupes = rows.filter((r) => r.isDuplicate).length;
  const cards = [
    ["प्रलंबित (Pending)", pending, Clock, "text-amber-600"],
    ["Verified", verified, CheckCircle2, "text-emerald-600"],
    ["Rejected", rejected, XCircle, "text-red-600"],
    ["डुप्लिकेट (Duplicates)", dupes, CopyX, "text-[#c2570a]"],
  ];
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="fee-summary-cards">
      {cards.map(([l, v, Icon, color]) => (
        <div key={l} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
          <Icon className={`h-6 w-6 ${color}`} />
          <div>
            <p className="text-[22px] font-bold text-[#1b5e3f] font-latin leading-none">{v}</p>
            <p className="mt-1 text-[12px] font-semibold text-slate-600">{l}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function RejectModal({ row, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason) { toast.error("कृपया कारण निवडा"); return; }
    if (reason === "Other" && !remarks.trim()) { toast.error("कृपया शेरा नमूद करा"); return; }
    setBusy(true);
    try {
      await api.accountantVerify(row.id, "rejected", reason, remarks);
      toast.success("पेमेंट Rejected");
      onDone();
    } catch (e) { toast.error(e.message || "त्रुटी"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="reject-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-[15px] font-bold text-red-600">पेमेंट नाकारा — {row.applicationNumber}</p>
          <button onClick={onClose} data-testid="reject-close" className="rounded-lg p-1 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <label className="mb-1 block text-[12.5px] font-semibold text-slate-600">कारण (Reason)</label>
        <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason"
          className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]">
          <option value="">— निवडा —</option>
          {REJECT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {reason === "Other" && (
          <>
            <label className="mb-1 block text-[12.5px] font-semibold text-slate-600">शेरा (Remarks) *</label>
            <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} data-testid="reject-remarks"
              className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]" />
          </>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-slate-300 px-4 py-2 text-[13px] font-semibold text-slate-600 hover:bg-slate-50">रद्द</button>
          <button onClick={submit} disabled={busy} data-testid="reject-confirm"
            className="rounded-full bg-red-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-red-700 disabled:opacity-60">{busy ? "..." : "Reject करा"}</button>
        </div>
      </div>
    </div>
  );
}

function ReviewModal({ row, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.rdcPaymentReview(row.id, note);
      toast.success("लेखापालाकडे पुनरावलोकनासाठी पाठवले");
      onDone();
    } catch (e) { toast.error(e.message || "त्रुटी"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="review-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-[15px] font-bold text-[#c2570a]">पुनरावलोकनासाठी पाठवा — {row.applicationNumber}</p>
          <button onClick={onClose} data-testid="review-close" className="rounded-lg p-1 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <p className="mb-2 text-[12.5px] text-slate-500">ही नोंद लेखापालाकडे पुन्हा तपासणीसाठी पाठवली जाईल.</p>
        <label className="mb-1 block text-[12.5px] font-semibold text-slate-600">टीप (ऐच्छिक)</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="review-note"
          className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-slate-300 px-4 py-2 text-[13px] font-semibold text-slate-600 hover:bg-slate-50">रद्द</button>
          <button onClick={submit} disabled={busy} data-testid="review-confirm"
            className="rounded-full bg-[#c2570a] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#a8490a] disabled:opacity-60">{busy ? "..." : "पाठवा"}</button>
        </div>
      </div>
    </div>
  );
}

// mode: "accountant" (verify/reject, pending-only queue) | "rdc" (read-only oversight + send-for-review)
export default function FeePaymentsView({ mode = "accountant" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [dupOnly, setDupOnly] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);
  const [reviewRow, setReviewRow] = useState(null);
  const [sub, setSub] = useState("pending"); // rdc sub-tab

  const loader = mode === "rdc" ? api.rdcPayments : api.accountantPayments;
  const load = () => {
    setLoading(true);
    loader().then(setRows).catch((e) => toast.error(e.message || "त्रुटी")).finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line

  const searched = useMemo(() => rows.filter((r) => {
    if (from && (!r.date || r.date.slice(0, 10) < from)) return false;
    if (to && (!r.date || r.date.slice(0, 10) > to)) return false;
    if (q) {
      const s = q.toLowerCase();
      if (![r.applicationNumber, r.name, r.utr].some((v) => (v || "").toLowerCase().includes(s))) return false;
    }
    return true;
  }), [rows, q, from, to]);

  const visible = useMemo(() => {
    if (mode === "accountant") {
      let v = searched.filter((r) => r.paymentStatus === "pending" || r.sentForReview);
      if (dupOnly) v = v.filter((r) => r.isDuplicate);
      return v;
    }
    // rdc sub-tabs
    if (sub === "verified") return searched.filter((r) => r.paymentStatus === "verified");
    if (sub === "rejected") return searched.filter((r) => r.paymentStatus === "rejected");
    if (sub === "duplicates") return searched.filter((r) => r.isDuplicate);
    return searched.filter((r) => r.paymentStatus === "pending" || r.sentForReview);
  }, [searched, mode, sub, dupOnly]);

  const verify = async (r) => {
    setSavingId(r.id);
    try {
      await api.accountantVerify(r.id, "verified");
      toast.success("पेमेंट Verified");
      load();
    } catch (e) { toast.error(e.message || "त्रुटी"); }
    setSavingId(null);
  };

  const exportCsv = () => {
    const head = ["Sr No", "Date", "Application ID", "Name", "UTR No", "Method", "Amount", "Status", "Duplicate", "Reject Reason", "Remarks"];
    const lines = visible.map((r, i) => [
      i + 1, fmtDate(r.date), r.applicationNumber, r.name, r.utr, r.method, r.amount,
      r.paymentStatus, r.isDuplicate ? "Yes" : "No", r.rejectReason, r.rejectRemarks,
    ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = [head.join(","), ...lines].join("\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `payment-utr-register-${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const subTabs = [["pending", "Pending"], ["verified", "Verified"], ["rejected", "Rejected"], ["duplicates", "Duplicates"]];

  return (
    <div data-testid={`fee-payments-${mode}`}>
      <SummaryCards rows={rows} />

      {mode === "rdc" && (
        <div className="mb-3 flex flex-wrap gap-2">
          {subTabs.map(([k, l]) => (
            <button key={k} onClick={() => setSub(k)} data-testid={`fee-subtab-${k}`}
              className={`rounded-full px-5 py-2 text-[13.5px] font-semibold transition-colors ${sub === k ? "bg-[#1b5e3f] text-white" : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="अर्ज क्र./नाव/UTR शोधा" data-testid="fee-search"
            className="w-full rounded-full border border-slate-300 py-2 pl-9 pr-3 text-[13.5px] outline-none focus:border-[#1b5e3f]" />
        </div>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-full border border-slate-300 px-3 py-2 text-[13px]" title="पासून" data-testid="fee-from" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-full border border-slate-300 px-3 py-2 text-[13px]" title="पर्यंत" data-testid="fee-to" />
        {mode === "accountant" && (
          <label className="flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-2 text-[13px] font-semibold text-slate-600">
            <input type="checkbox" checked={dupOnly} onChange={(e) => setDupOnly(e.target.checked)} data-testid="fee-dup-only" className="h-4 w-4 accent-[#c2570a]" />
            फक्त डुप्लिकेट
          </label>
        )}
        <button onClick={load} className="flex items-center gap-1 rounded-full border border-slate-300 px-3 py-2 text-[13px] font-semibold text-slate-600 hover:bg-slate-50"><RefreshCw className="h-4 w-4" /></button>
        <button onClick={exportCsv} data-testid="fee-export" className="flex items-center gap-1.5 rounded-full bg-[#1b5e3f] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#144d33]"><Download className="h-4 w-4" /> Excel/CSV</button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-[13px]">
          <thead className="bg-[#1b5e3f]/5 text-left text-[12px] uppercase text-slate-500">
            <tr>
              {["अ.क्र.", "दिनांक", "अर्ज क्र.", "नाव", "पेमेंटची पद्धत", "UTR क्र.", "रक्कम", ...(mode === "rdc" ? ["स्थिती"] : []), "पुरावा", "कृती"].map((h) => (
                <th key={h} className="whitespace-nowrap px-3 py-2.5 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="py-10 text-center text-slate-400">लोड होत आहे...</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={10} className="py-10 text-center text-slate-400" data-testid="fee-empty">कोणतीही नोंद नाही</td></tr>
            ) : visible.map((r, i) => (
              <tr key={r.id} className={`border-t border-slate-100 ${r.isDuplicate ? "bg-red-50/50" : ""}`} data-testid={`payment-row-${r.applicationNumber}`}>
                <td className="px-3 py-2.5 text-slate-500">{i + 1}</td>
                <td className="whitespace-nowrap px-3 py-2.5">{fmtDate(r.date)}</td>
                <td className="px-3 py-2.5 font-semibold text-[#1b5e3f] font-latin">{r.applicationNumber}</td>
                <td className="px-3 py-2.5">{r.name || "—"}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">{r.method || "—"}</td>
                <td className="px-3 py-2.5">
                  <span className="font-latin">{r.utr || "—"}</span>
                  {r.isDuplicate && (
                    <span data-testid={`dup-badge-${r.applicationNumber}`} className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10.5px] font-bold text-red-600">
                      <AlertTriangle className="h-3 w-3" /> डुप्लिकेट
                    </span>
                  )}
                  {r.sentForReview && (
                    <span data-testid={`review-badge-${r.applicationNumber}`} className="ml-1.5 inline-flex items-center rounded-full bg-[#c2570a]/15 px-2 py-0.5 text-[10.5px] font-bold text-[#c2570a]">पुनरावलोकन</span>
                  )}
                  {r.rejectReason && <div className="mt-0.5 text-[11px] text-red-500">{r.rejectReason}{r.rejectRemarks ? ` — ${r.rejectRemarks}` : ""}</div>}
                </td>
                <td className="px-3 py-2.5 font-latin">₹{r.amount}</td>
                {mode === "rdc" && <td className="px-3 py-2.5"><StatusPill s={r.paymentStatus} /></td>}
                <td className="px-3 py-2.5"><ProofBadge row={r} /></td>
                <td className="px-3 py-2.5">
                  {mode === "accountant" ? (
                    <div className="flex gap-1.5">
                      <button onClick={() => verify(r)} disabled={savingId === r.id} data-testid={`verify-${r.applicationNumber}`}
                        className="rounded-full bg-emerald-600 px-3 py-1 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">Verify</button>
                      <button onClick={() => setRejectRow(r)} disabled={savingId === r.id} data-testid={`reject-${r.applicationNumber}`}
                        className="rounded-full bg-red-600 px-3 py-1 text-[12px] font-semibold text-white hover:bg-red-700 disabled:opacity-60">Reject</button>
                    </div>
                  ) : (
                    <button onClick={() => setReviewRow(r)} data-testid={`send-review-${r.applicationNumber}`}
                      className="inline-flex items-center gap-1 rounded-full bg-[#c2570a] px-3 py-1 text-[12px] font-semibold text-white hover:bg-[#a8490a]">
                      <Send className="h-3.5 w-3.5" /> Send for Review
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rejectRow && <RejectModal row={rejectRow} onClose={() => setRejectRow(null)} onDone={() => { setRejectRow(null); load(); }} />}
      {reviewRow && <ReviewModal row={reviewRow} onClose={() => setReviewRow(null)} onDone={() => { setReviewRow(null); load(); }} />}
    </div>
  );
}
